import { useFormContext, useFormState, useWatch } from 'react-hook-form';
import { dsClass } from '../../../common/constants/dsClasses.js';
import { useSearchParams } from '../../../common/hooks/useSearchParams.js';
import { selectMsg, t } from '../../../common/i18n/index.js';
import {
  CREATE_NEW_CONTACT_OPTION_VALUE,
  ContactDropdown,
} from '../../../common/react-hook-form/fields/ContactDropdown.js';
import { getCompanyName } from '../../../common/utils/accountUtils.js';
import { useAuth } from '../../../public/site/AuthProvider.js';
import { useSessionData } from '../../../public/site/SessionDataProvider.js';
import { Loading } from '../../Loading/index.js';
import { CreateNewUserButton } from '../../PlaceOrder/steps/products/PlaceOrderContactOrPurpose/components/CreateNewUserButton';
import { CreateContact } from './CreateContact.js';
import type { Contact } from '../../../generated/api/contact.js';
import type { ContactHeader } from '../../../generated/api/contactHeader.js';

interface CreateOrSelectContactProps {
  companyName?: string;
  contacts?: Contact[] | ContactHeader[];
  fieldPrefix?: string;
  isEmailMandatory?: boolean;
  isPhoneNumberMandatory?: boolean;
  onContactChange?: (selectedContactId: string) => void;
  /** Callback for explicit user creation - if provided, shows "Create new user" button */
  onCreateUser?: () => void;
  /** Whether user creation is in progress (shows loading state on button) */
  isCreatingUser?: boolean;
  useCreateContactModal?: boolean;
  /** When false, selecting a contact is not required. */
  isContactMandatory?: boolean;
}

export const CreateOrSelectContact = ({
  companyName: companyNameOverride,
  contacts,
  fieldPrefix = '',
  isEmailMandatory = true,
  isPhoneNumberMandatory = true,
  onContactChange,
  onCreateUser,
  isCreatingUser = false,
  useCreateContactModal = false,
  isContactMandatory = true,
}: CreateOrSelectContactProps) => {
  const { resetField, setValue } = useFormContext();
  const { defaultValues } = useFormState();
  const { mdmId } = useSearchParams<{ mdmId?: string }>();
  const { authenticatedUser } = useAuth();
  const companyInfo = useSessionData().companyInfo;

  const companyName = companyNameOverride || getCompanyName(authenticatedUser, mdmId) || companyInfo?.companyName;

  const contactId = useWatch({ name: `${fieldPrefix}contactId` });

  if (!contacts) {
    return <Loading />;
  }
  const onChangeContact = (value: string) => {
    const defaultContactId = defaultValues?.[`${fieldPrefix}contactId`];
    if (value === defaultContactId) {
      resetField(`${fieldPrefix}costCenter`);
      resetField(`${fieldPrefix}employeeNumber`);
    } else if (value === CREATE_NEW_CONTACT_OPTION_VALUE) {
      setValue(`${fieldPrefix}costCenter`, '');
      setValue(`${fieldPrefix}employeeNumber`, '');
      if (useCreateContactModal && onCreateUser) {
        onCreateUser();
        return;
      }
    } else {
      const selectedContact = contacts?.find(c => c.contactId === value);
      const contact = selectedContact as Contact;
      const contactHeader = selectedContact as ContactHeader;
      if (selectedContact && 'person' in selectedContact) {
        setValue(`${fieldPrefix}costCenter`, contact?.person?.costCenter ?? '');
        setValue(`${fieldPrefix}employeeNumber`, selectedContact?.person?.employeeNumber ?? '');
      } else {
        setValue(`${fieldPrefix}costCenter`, contactHeader?.costCenter ?? '');
        setValue(`${fieldPrefix}employeeNumber`, contactHeader?.referenceOrAdditionalInformation ?? '');
      }
    }
    if (onContactChange) {
      onContactChange(value);
    }
  };

  return (
    <>
      <div className="of-purpose-of-use__contact_dropdown">
        <ContactDropdown
          contacts={contacts}
          name={`${fieldPrefix}contactId`}
          createNewDialog={false}
          labelText={`${companyName}${t.S6U5('s users')}`}
          labelClass={`${dsClass.FONT_SIZE_SMALLER} ${dsClass.FONT_STYLE_NORMAL}`}
          placeHolderText={t.QRYV(selectMsg)}
          canAddNewContacts={true}
          onChangeContact={onChangeContact}
          required={isContactMandatory}
        />
      </div>
      {contactId === CREATE_NEW_CONTACT_OPTION_VALUE && !useCreateContactModal && (
        <>
          <CreateContact
            fieldPrefix={fieldPrefix}
            isEmailMandatory={isEmailMandatory}
            isPhoneNumberMandatory={isPhoneNumberMandatory}
          />
          {onCreateUser && <CreateNewUserButton onClick={onCreateUser} isLoading={isCreatingUser} />}
        </>
      )}
    </>
  );
};
